import React, { useCallback, useContext, useEffect, useRef } from 'react';
import {
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useReactFlow,
  type Connection,
  type EdgeChange,
  type IsValidConnection,
  type NodeChange,
  type OnSelectionChangeParams,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme } from 'styled-components';
import { EditorContext } from './EditorContext';
import { useNodeDefinitions } from '../hooks/useNodeDefinitions';
import {
  PALETTE_ADD_EVENT,
  PALETTE_DRAG_LABEL_MIME,
  PALETTE_DRAG_MIME,
  paletteTypeToNode,
  type PaletteAddEventDetail,
} from './paletteDrop';
import type { ProcessEdge, ProcessNode } from './graphTypes';
import { nodeTypes } from './nodes/nodeTypes';
import { edgeTypes } from './edges/edgeTypes';
import ConnectionLine from './edges/ConnectionLine';
import { isValidConnection as isValidProcessConnection } from './connectionValidation';
import { minimapNodeColor } from './minimapNodeColor';
import {
  CanvasViewport,
  EmptyCanvasCta,
  EmptyCanvasCtaSub,
  EmptyCanvasCtaTitle,
} from './GraphCanvas.styled';

interface GraphCanvasProps {
  nodes: ProcessNode[];
  edges: ProcessEdge[];
  onNodesChange: (changes: NodeChange<ProcessNode>[]) => void;
  onEdgesChange: (changes: EdgeChange<ProcessEdge>[]) => void;
  onConnect: (connection: Connection) => void;
  /** Called once a palette drop has been resolved into a fresh node. */
  onPaletteDrop?: (node: ProcessNode) => void;
  /**
   * Called whenever the user's selection changes (single, multi, box-select,
   * or cleared). The parent uses this to power inspector / inline-actions
   * UIs without re-implementing the selection state machine.
   */
  onSelectionChange?: (params: OnSelectionChangeParams) => void;
  /** Optional ref so the parent can grab `.fitView()` etc. */
  onInit?: (instance: ReactFlowInstance<ProcessNode, ProcessEdge>) => void;
  /**
   * Read-only canvases skip the connect handler + drop handler so the user
   * can't mutate the graph. We still render the `MiniMap` so the layout
   * stays legible.
   */
  readOnly?: boolean;
  /**
   * Whether nodes are draggable. Defaults to {@code !readOnly} so existing
   * call sites get the previous behaviour, but Published processes pass
   * {@code true} explicitly to allow ephemeral, non-persisted layout
   * tweaks without unlocking every other editing affordance.
   */
  nodesDraggable?: boolean;
  /**
   * Receives a getter that returns the last mouse position over the
   * canvas in canvas-world coordinates (i.e. after applying React
   * Flow's pan + zoom transform), or {@code null} when the pointer is
   * not over the canvas. Used by the editor's clipboard to paste at
   * the cursor; consumers ignore it when they only want the default
   * `+40/+40` placement.
   */
  onCursorWorldPosRef?: (
    getter: () => { x: number; y: number } | null,
  ) => void;
  /** Optional test id forwarded to the viewport wrapper. */
  dataTestId?: string;
  /**
   * Optional overlays to mount on top of the canvas (above the React
   * Flow viewport, below pointer-event-capturing dialogs). Used by
   * {@code ProcessEditorPage} to drop the floating canvas toolbars and
   * the Run-history sheet over the canvas without leaking those
   * concerns down into the canvas itself.
   */
  overlays?: React.ReactNode;
}

// Click-to-add placement constants. When the user clicks a quick-add
// button on the rail (rather than dragging), we drop the node at the
// canvas viewport's centre. If that spot would collide with an
// existing node, we nudge by `CLICK_ADD_OFFSET` repeatedly until we
// find a clear position. The "near" thresholds are slightly larger
// than the typical task-node footprint (≈280×120) so the new node
// never visually overlaps a neighbour, even on snapped grid lines.
const CLICK_ADD_NEAR_X = 60;
const CLICK_ADD_NEAR_Y = 40;
const CLICK_ADD_OFFSET = 20;
const CLICK_ADD_MAX_NUDGES = 24;

// Hide React Flow's bottom-right attribution badge so the canvas reads
// as our own surface. `proOptions.hideAttribution` is the documented
// opt-out switch — note xyflow's MIT-with-attribution license formally
// expects Pro subscribers to flip it; we accept that trade-off here.
// Hoisted to module scope because the value never changes — there's no
// reason to allocate (or memoise) it once per component mount.
const PRO_OPTIONS = { hideAttribution: true } as const;

// `multiSelectionKeyCode` and `deleteKeyCode` accept arrays so we can cover
// both Mac and Windows / Linux idioms in one shot.
const MULTI_SELECTION_KEYS = ['Shift', 'Meta', 'Control'];
const DELETE_KEYS = ['Backspace', 'Delete'];

// Right-click-to-pan: only button 2 (right mouse button) drags the canvas.
// Left-drag on empty pane draws a box-select marquee instead.
const PAN_ON_DRAG_BUTTONS: number[] = [2];

// xyflow's default 0.5 floor clips out before mid-size graphs fit
// on screen (AIB-7062). Drop it so large workflows survey end-to-end.
const MIN_ZOOM = 0.1;
const FIT_VIEW_OPTIONS = { padding: 0.2, minZoom: MIN_ZOOM } as const;

const InnerCanvas: React.FC<GraphCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onPaletteDrop,
  onSelectionChange,
  onInit,
  readOnly = false,
  nodesDraggable,
  onCursorWorldPosRef,
  dataTestId = 'process-graph-canvas',
  overlays,
}) => {
  const dragEnabled = nodesDraggable ?? !readOnly;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Tracks the last mouse position seen on the canvas in screen
  // (clientX/clientY) coordinates. Cleared when the pointer leaves the
  // canvas so paste-at-cursor can fall back to the default offset
  // when the user triggered the shortcut from off-canvas focus.
  const lastClientPosRef = useRef<{ x: number; y: number } | null>(null);
  const { screenToFlowPosition } = useReactFlow<ProcessNode, ProcessEdge>();
  const theme = useTheme() as
    | { colors?: Record<string, string | undefined> }
    | undefined;
  // Read overlay-visibility flags directly from the editor context when
  // available -- when the canvas is mounted standalone (e.g. story /
  // unit-test harness) the context is absent and both overlays default
  // to ON, matching the legacy behaviour.
  const editor = useContext(EditorContext);
  const gridOn = editor?.gridOn ?? true;
  const miniOn = editor?.miniOn ?? true;
  // Catalog is the single source of truth for fresh-node defaults
  // (label, config, ports). The hook's React Query cache is hard-pinned
  // for the page lifetime, so this is a synchronous read after the
  // first paint — palette drops simply no-op until the first fetch
  // resolves (matches NodeLibrary's gating).
  const { data: nodeDefinitions } = useNodeDefinitions();
  // Minimap palette mirrors the prototype's `.ns-minimap` SVG (NodeShowcase
  // .jsx:3046-3085) — the SVG bg matches the toolbar pill so the rounded
  // 24px shell reads as one surface, the mask outside the viewport dims
  // through the canvas backdrop, and the viewport rectangle is stroked
  // in the same muted grey-purple as the resting node dots.
  const minimapBg = theme?.colors?.surface ?? '#13131B';
  const minimapMask = `${theme?.colors?.surfaceContainerLowest ?? '#0D0D16'}cc`;
  // `#7c7d96` mirrors the prototype's viewport-rect stroke. It sits
  // between `colors.outline` (too dark for a thin 1.3px stroke to read)
  // and `colors.onSurfaceVariant` (too light, would compete with dots),
  // so it stays a literal here rather than mapping to a theme token.
  const minimapViewportStroke = '#7C7D96';

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    lastClientPosRef.current = { x: event.clientX, y: event.clientY };
  }, []);

  const onMouseLeave = useCallback(() => {
    // Drop the cached coordinate when the pointer leaves the canvas;
    // otherwise a paste-at-cursor triggered from a side-panel input
    // would still land at the stale (last-on-canvas) position.
    lastClientPosRef.current = null;
  }, []);

  // Hand a getter back to the parent so the editor's clipboard hook
  // can resolve "current cursor world position" lazily, on demand,
  // without us re-publishing the value on every mousemove.
  useEffect(() => {
    if (!onCursorWorldPosRef) return;
    onCursorWorldPosRef(() => {
      const client = lastClientPosRef.current;
      if (!client) return null;
      return screenToFlowPosition(client);
    });
  }, [onCursorWorldPosRef, screenToFlowPosition]);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (readOnly || !onPaletteDrop) return;
      const raw = event.dataTransfer.getData(PALETTE_DRAG_MIME);
      if (!raw) return;
      if (!nodeDefinitions) return;
      // The label MIME is best-effort: when missing (legacy payloads,
      // programmatic drops), `paletteTypeToNode` falls back to the
      // catalog-derived default ("Task" / "Input" / ...).
      const defaultLabel =
        event.dataTransfer.getData(PALETTE_DRAG_LABEL_MIME) || undefined;
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const node = paletteTypeToNode(raw, {
        position,
        defaultLabel,
        definitions: nodeDefinitions,
      });
      if (!node) return;
      onPaletteDrop(node);
    },
    [onPaletteDrop, readOnly, screenToFlowPosition, nodeDefinitions],
  );

  // Click-to-add: the rail's quick-add buttons fire a window CustomEvent
  // (see `dispatchPaletteAddEvent`) when clicked instead of dragged.
  // We listen here — the canvas is the only place that has both the
  // current node positions AND React Flow's `screenToFlowPosition`,
  // so it owns the placement logic. The new node lands at the
  // viewport centre; if that spot would overlap an existing node,
  // we nudge diagonally by `CLICK_ADD_OFFSET` until clear.
  useEffect(() => {
    if (readOnly || !onPaletteDrop) return undefined;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<PaletteAddEventDetail>).detail;
      if (!detail?.paletteType) return;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      let position = screenToFlowPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
      // Avoid silently stacking nodes on top of each other if the user
      // mashes the rail button — nudge until we find a clear spot.
      for (
        let attempts = 0;
        attempts < CLICK_ADD_MAX_NUDGES &&
        nodes.some(
          (n) =>
            Math.abs(n.position.x - position.x) < CLICK_ADD_NEAR_X &&
            Math.abs(n.position.y - position.y) < CLICK_ADD_NEAR_Y,
        );
        attempts++
      ) {
        position = {
          x: position.x + CLICK_ADD_OFFSET,
          y: position.y + CLICK_ADD_OFFSET,
        };
      }
      if (!nodeDefinitions) return;
      const node = paletteTypeToNode(detail.paletteType, {
        position,
        defaultLabel: detail.label || undefined,
        definitions: nodeDefinitions,
      });
      if (node) onPaletteDrop(node);
    };
    globalThis.addEventListener(PALETTE_ADD_EVENT, handler);
    return () => globalThis.removeEventListener(PALETTE_ADD_EVENT, handler);
  }, [nodes, onPaletteDrop, readOnly, screenToFlowPosition, nodeDefinitions]);

  // Bind the validator to the current nodes + edges so dedupe and direction
  // checks stay accurate as the graph mutates. Nodes drive the port-by-id
  // lookups (output → input direction enforcement); edges drive dedupe.
  const isValidConnection = useCallback<IsValidConnection<ProcessEdge>>(
    (connection) => isValidProcessConnection(connection, edges, nodes),
    [edges, nodes],
  );

  const minimapColor = useCallback(
    (node: ProcessNode) => minimapNodeColor(node, theme),
    [theme],
  );

  const isEmpty = nodes.length === 0;

  return (
    <CanvasViewport
      ref={wrapperRef}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      data-testid={dataTestId}
      data-readonly={readOnly ? 'true' : 'false'}
      data-grid={gridOn ? 'on' : 'off'}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        connectionLineComponent={ConnectionLine}
        isValidConnection={readOnly ? undefined : isValidConnection}
        onSelectionChange={onSelectionChange}
        nodesDraggable={dragEnabled}
        nodesConnectable={!readOnly}
        elementsSelectable
        // Interaction model (matching the NavLegend tooltip):
        //   Left-click / drag on pane → box-select marquee
        //   Middle scroll             → zoom
        //   Right-drag                → pan the canvas
        panOnDrag={PAN_ON_DRAG_BUTTONS}
        selectionOnDrag={!readOnly}
        selectionMode={SelectionMode.Partial}
        snapToGrid={false}
        // Backspace + Delete clear the current selection. Disabled in
        // read-only mode so the user can't blow away a locked graph.
        deleteKeyCode={readOnly ? null : DELETE_KEYS}
        multiSelectionKeyCode={MULTI_SELECTION_KEYS}
        minZoom={MIN_ZOOM}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        proOptions={PRO_OPTIONS}
        onInit={onInit}
      >
        {miniOn && (
          <MiniMap
            pannable
            zoomable
            bgColor={minimapBg}
            maskColor={minimapMask}
            maskStrokeColor={minimapViewportStroke}
            maskStrokeWidth={1.3}
            nodeColor={minimapColor}
            nodeBorderRadius={2}
            nodeStrokeWidth={0}
          />
        )}
        {isEmpty && (
          <Panel position="top-center">
            <EmptyCanvasCta data-testid={`${dataTestId}__empty-cta`}>
              <EmptyCanvasCtaTitle>
                This process is empty
              </EmptyCanvasCtaTitle>
              <EmptyCanvasCtaSub>
                {readOnly
                  ? 'Duplicate this process to start adding nodes.'
                  : 'Click a node on the left rail to drop it here, or drag one onto the canvas.'}
              </EmptyCanvasCtaSub>
            </EmptyCanvasCta>
          </Panel>
        )}
      </ReactFlow>
      {overlays}
    </CanvasViewport>
  );
};

/**
 * Public entry. Wraps the inner canvas in `<ReactFlowProvider>` so all the
 * React-Flow hooks (e.g. `useReactFlow`) work even if the parent forgot to
 * supply one. The provider is idempotent so wrapping again at a higher
 * level (e.g. a test that needs to call `useReactFlow` outside the canvas)
 * is harmless.
 */
const GraphCanvas: React.FC<GraphCanvasProps> = (props) => (
  <ReactFlowProvider>
    <InnerCanvas {...props} />
  </ReactFlowProvider>
);

export default GraphCanvas;
